import { FormDraft, MobileFormStructure } from '../backbone/features/mobileForm/MobileFormStructure/models';
import { MobileMediaField } from '../backbone/shared/models/MobileMediaField';
import { ModalOptionsView } from '../backbone/shared/views/ModalOptionsView';
import { CordovaPlugins } from '../shared/CordovaPlugins';
import { Paginator } from './shared/forms';
import { MobileTasks, MyTasks, MobileTask } from './tasks/models';
import { isAndroid, isConnected, isIos, isWeb, isWindows } from '../shared/utils/is';
import {
    actionSheet,
    getActiveToastView,
    showOfflineMessage,
    showUploadProgress,
    showErrorMessage
} from '../shared/utils/notifications';
/**
 * One record represending a cacheable object.  It has an id,
 * a title, and a date for cache invalidation.
 */
export const LocalRecord = Backbone.Model.extend({
    defaults: {
        id: null,
        title: null,
        date: null, // this is used for cache invalidation
        is_synced: false // has this record been uploaded/synced to the server
    },

    get_age_minutes: function () {
        const date_added = this.get('date') ? new Date(this.get('date')) : null;

        if (!date_added) {
            // this shouldn't happen, but if we don't have the date, we can't know how long its been here
            return 0;
        }

        return moment().diff(date_added, 'minutes');
    }
});

export const FormStructureLocalRecord = LocalRecord.extend({
    defaults: $.extend({}, LocalRecord.prototype.defaults, {
        is_secure: false,
        date_modified: null, // moment
        account_name: null
    })
});

/**
 * This is going to be a genric object that allows us to store arrays of
 * Backbone collections.  It contains a persistent cache that uses localStorage
 */
export const LocalRecords = Backbone.Collection.extend({
    klass: null, // what type of Backbone Collection will you be caching?
    comparator: 'date', // important for cache invalidation, dont change this
    ttl: 0, // default TTL for records before a refetch happens
    model: LocalRecord,

    cache_key: null, // i.e. 'rdbms_id' or 'account_tag_id'
    cache_key_of_keys: null, // make the cache key plural, i.e. account_tag_ids
    max_items: 0, // set to null or zero for infinite

    reverse_order: false,
    is_uploading: false, // If true, is currently uploading submissions
    keep_records: false, // If true, don't remove the records once they are uploaded, mark them as synced.

    /**
     * always construct this from the local store
     */
    constructor: function (options) {
        $.extend(this, options);
        this.cache_key_of_keys = this.cache_key + 's';

        Backbone.Collection.apply(this);
        var parent = this;
        $f.mobile.storage.get(this.cache_key_of_keys, function (records) {
            if (!records) {
                return;
            }

            // this is for converting the old way of storing templates to the new way.
            // save to remove later in 2014 after more releases
            if (!(records instanceof Array)) {
                var arr = [];
                $.each(records, function (key, value) {
                    arr.push({ id: key, title: value });
                });
                records = arr;
            }

            _.each(records, function (record) {
                parent.add(new parent.model(record));
            });
        });
    },

    /**
     * This the way that we grab Collections from localStorage.  This will
     * return a callback with the Backbone collection as the argument.
     */
    get_record: function (id, options, success, error, force, never_miss) {
        options = options || {};
        success = success || function () {};
        error =
            error ||
            function (err) {
                $f.mobile.logging.log_error('Error while retreiving a record', {
                    error: err
                });
            };
        force = force || false;
        never_miss = never_miss || false;

        var this_ = this;
        var model = this_.get(id);

        if (model && model.get('date') && this.ttl != 0) {
            // see if its stale
            var now = new Date();
            if (now - model.get('date') > this.ttl) {
                force = true;
            }
        }

        var cache_key = `${this.cache_key}_${id}`;
        $f.mobile.storage.get(
            cache_key,
            function (collections_json) {
                var new_collection = collections_json ? new this_.klass(collections_json) : null;
                if (new_collection && !force) {
                    success(new_collection);
                    this_._update_date_last_accessed(id); // tick this so it wont get garbage collected
                    return;
                }

                this_._fetch_record(id, options, success, error, never_miss ? new_collection : null);
            },
            function () {
                this_._fetch_record(id, options, success, error); // lets try and recover, i.e. pull again from the server
            }
        );
    },

    /**
     * Given an id or array of ids, remove them from our collection and from localStorage
     */
    remove_records: function (ids, callback = () => {}) {
        if (!(ids instanceof Array)) {
            ids = [ids];
        }

        // shallow clone the IDs
        ids = [...ids];

        if (!ids?.length) {
            return callback();
        }

        var _recurse = (cb) => {
            if (ids.length === 0) {
                return cb();
            }

            var id = ids.pop();
            this.remove(id);
            this.save(
                () => {
                    $f.mobile.storage.remove(
                        `${this.cache_key}_${id}`,
                        () => {
                            _recurse(cb);
                        },
                        () => {
                            $f.mobile.logging.log_error('Unable to remove local record from storage', {
                                klass: this.klass
                            });
                            _recurse(cb); // failed to remove, oh well. We can only move forward.
                        }
                    );
                },
                () => {
                    $f.mobile.logging.log_error('Unable to remove_records()', { klass: this.klass });
                    _recurse(cb);
                }
            );
        };

        // removing local records from disk happens asynchronously, so we'll use a recursive loop to wait for each
        // to be removed before moving on to the next. Once we're done, we'll do a final garbage collect.
        _recurse(() => {
            // once we're done removing, let's garbage collect
            this._garbage_collect(); // after all are done
            callback();
        });
    },

    /**
     * Delete all from disk and clear this instance
     * in memory
     */
    clear_all: function (callback) {
        this.remove_records(this.pluck('id'), callback);
    },

    /**
     * Our super class will call this when all other caches fail, or if there is a force.
     *
     * @param {int} id                      The ID of the object
     * @param {Object} options              Options
     * @param {Function} success_callback
     * @param {Function} error_callback
     * @param {Object} cached_object        (optional)  If the server errors, we still return this object so
     *                                      so that we almost never get a cache miss
     */
    _fetch_record: function (id, options, success_callback, error_callback, cached_object) {
        options = options || {};
        success_callback = success_callback || function () {};
        error_callback = error_callback || function () {};

        $.extend(options, { id: id });

        var this_ = this;
        var collection = new this.klass(options);
        var now = new Date();
        var _error_callback = error_callback;

        error_callback = function () {
            $f.mobile.logging.log_error('Unable to fetch collection in _fetch_record()', { klass: this_.klass });
            _error_callback();
        };

        collection.fetch({
            success: function (_collection) {
                // @author:david for some reason, just using this_.add() doesn't update if there is an existing model.
                // Instead, proactively calling findWhere().set() instead if necessary:
                if (this_.findWhere({ id: _collection.id })) {
                    this_.findWhere({ id: _collection.id }).set('date', now.getTime());
                } else {
                    this_.add(new LocalRecord({ id: _collection.id, date: now.getTime() }));
                }

                this_.save(
                    function () {
                        $f.mobile.storage.set(
                            `${this_.cache_key}_${_collection.id}`,
                            _collection.toJSON(),
                            function () {
                                this_._garbage_collect();
                                success_callback(_collection);
                            },
                            function () {
                                error_callback();
                            }
                        );
                    },
                    function () {
                        error_callback();
                    }
                );
            },

            error: function () {
                if (cached_object) {
                    success_callback(cached_object);
                    return;
                }

                error_callback();
            }
        });
    },

    /**
     * Anytime we call a fetch we need to garbage collect to make sure that we dont
     * put more data in here than the caller would like
     */
    _garbage_collect: function () {
        var this_ = this;
        if (!this.max_items || this.max_items <= 0 || this.length <= this.max_items) {
            return;
        }

        if (this.reverse_order) {
            this.remove_records(this.slice(this.max_items, this.length));
            return;
        }

        var ids_to_remove = [];
        this.each(function (model) {
            if (this_.length <= this_.max_items) {
                return false; // cache doesnt need clearing, return
            }

            ids_to_remove.push(model.id);
        });

        this.remove_records(ids_to_remove);
    },

    /**
     * Anytime we get a cache hit we need to update the date stamp so that this
     * collection is less likely to be garbage collected
     */
    _update_date_last_accessed: function (id) {
        var _model = this.get(id);
        if (!_model) {
            return;
        }

        _model.set('date', new Date().getTime()); // update our records so it will stay in cache
        // @jira:MOBILE-810 look into batching this save for performance
        this.save();
    },

    save: function (success_cb, error_cb) {
        success_cb = success_cb || function () {};
        error_cb = error_cb || function () {};

        var key = this.cache_key_of_keys;
        $f.mobile.storage.set(
            key,
            this.toJSON(),
            function () {
                // just to make sure we've actually saved it, let's verify by getting it back @jira:FORMS-365
                $f.mobile.storage.get(
                    key,
                    function (result) {
                        if (!result) {
                            $f.mobile.logging.log_error(
                                'Failed to confirm that local record was saved to disk (1). Failing.'
                            );
                            return error_cb();
                        }

                        success_cb();
                    },
                    function () {
                        $f.mobile.logging.log_error(
                            'Failed to confirm that local record was saved to disk (2). Failing.'
                        );
                        error_cb();
                    }
                );
            },
            function () {
                error_cb();
            }
        );
    },

    /**
     * The following methods are used for uploading
     */

    set_is_uploading: function (is_uploading) {
        this.is_uploading = is_uploading;
        showUploadProgress();
    },

    _error_wrapper: function (error_fn) {
        this.set_is_uploading(false);
        error_fn && error_fn();
    },

    /**
     * Also, subclasses must have an update_record()
     */
    // update_record: function(model, callback) {},

    /**
     * Get the unique identifier for a pending upload. Some implementations
     * use pending_upload.get('id'), and other's have a special guid that
     * should be used. For example, this is used to remove a record from
     * local_records.
     * @return {int} The unique identifier for the given pending_upload
     */
    get_upload_guid: function (pending_upload) {
        throw new Error('Programmer error: get_upload_guid() not implemented');
    },

    /**
     * Return the list of indexes that should be uploaded/synced to the server.
     *
     * @return {Array} - array of ids
     */
    get_indexes_for_upload: function () {
        var this_ = this;
        var indexes = [];
        this.forEach(function (obj, idx) {
            if (!this_.keep_records) {
                // if we don't keep the local records after upload, then all things should be uploaded/cleared.
                indexes.push(idx);
                return;
            }
            if (obj.get('is_synced') === false) {
                indexes.push(idx);
            }
        });
        return indexes;
    },

    /**
     * Upload all submissions in PendingFormSubmissions Local Record. This is what external views call.
     */
    upload: function (success, error) {
        success = success || function () {};
        error = error || function () {};
        var this_ = this;

        // Check for update_record()
        if (!this.update_record) {
            throw new Error('Programmer Error: update_record() not implemented.');
        }

        if (this.is_uploading) {
            showErrorMessage(_t('mobile_message_show_upload_in_progress_message'));
            showUploadProgress();
            return;
        }

        if (!isConnected()) {
            showOfflineMessage();
            showUploadProgress();
            $f.application.vent.trigger('mobile:set_num_uploads');
            return;
        }

        showUploadProgress(true);

        // Start the actual upload!
        this_.set_is_uploading(true);
        this_._upload(
            this.get_indexes_for_upload(),
            function () {
                this_.set_is_uploading(false);
                success();
                return;
            },
            function () {
                $f.application.vent.trigger('mobile:set_num_uploads');
                this_._error_wrapper(error);
                return;
            }
        );
    },

    _get_field_models: function (pending_upload) {
        throw new Error('Programmer Error: _get_field_models() not implemented.');
    },

    prepare_pending_upload_for_submission: function (pending_upload) {
        var field_models = this._get_field_models(pending_upload);

        _.each(field_models, function (model) {
            model.prepare_for_submission();
        });
    },

    /**
     * Will handle processing one record in the LocalRecord, recursively uploads records according to the indexes passed in.
     *
     * @param indexes {Array} - array indexes to upload
     * @param success {cb}
     * @param error {cb}
     */
    _upload: function (indexes, success, error) {
        success = success || function () {};
        error = error || function () {};

        var this_ = this;

        // This are no more pending submissions in local record
        if (indexes.length === 0) {
            return success();
        }

        var index = indexes.shift(); // take the first one off;
        var local_record = this.at(index);

        if (!local_record) {
            return success();
        }

        // add the record and kick off the upload
        this.get_record(
            local_record.get('id'),
            null,
            function (pending_upload) {
                if (!pending_upload) {
                    $f.mobile.logging.log_error('Unable to retrieve local record from disk', {
                        local_record_id: local_record.get('id')
                    });

                    // unfortunately there's nothing we can do here, so we'll call success :( // @jira:MOBILE-633
                    return this_.remove_records(local_record.get('id'), success);
                }

                var parent_upload_guid = this_.get_upload_guid(pending_upload);

                var submit = function () {
                    this_.prepare_pending_upload_for_submission(pending_upload);

                    this_.update_record(pending_upload, function () {
                        this_._submit_pending_upload(pending_upload).then(
                            function () {
                                $f.mobile.uploads.clear_pending_file_uploads(parent_upload_guid);
                                this_._upload(indexes, success, error);
                            },
                            function () {
                                this_._error_wrapper(error);
                            }
                        );
                    });
                };

                // kick off any uploads that aren't already started
                this_._create_pending_file_uploads(pending_upload);

                if (!$f.mobile.uploads.any_remaining_uploads(parent_upload_guid)) {
                    return submit();
                }

                $f.mobile.uploads.set_upload_listener(
                    parent_upload_guid,
                    function (upload) {
                        if ($f.mobile.uploads.any_remaining_uploads(parent_upload_guid)) {
                            return; // still have more to upload
                        }

                        // all files have uploaded for this submission!
                        $f.mobile.uploads.remove_upload_listener(parent_upload_guid);

                        submit();
                    },
                    function () {
                        if (!this_.is_uploading) {
                            // this error callback will fire for each file we have, but we only want to call the local_record
                            // error callback once. If we're no longer uploading, we've already fired it.
                            return;
                        }

                        this_._error_wrapper(error);
                    }
                );

                // this has to be done here in case there's already been an error so that stop_file_uploads() will call
                // the error callback (above) and cancel the form upload
                $f.mobile.uploads.upload_pending_files();
            },
            function () {
                // failed to get record
                this_._error_wrapper(error);
            }
        );
    },

    /**
     * Handle logic for success response from the server to the upload,
     * must resolve the dfd
     *
     * @param guid - guid of the record
     * @param dfd - $.Deferred()
     * @param response - server response
     * @private
     */
    _on_upload_success: function (pending_upload, guid, dfd, response) {
        this.remove_records(guid, function () {
            dfd.resolve();
        });
    },

    /**
     * Handle logic for error response from the server to the upload
     * Must resolve or reject the dfd
     *
     * @param guid - guid of the record
     * @param dfd - $.Deferred()
     * @param response - server response
     * @private
     */
    _on_upload_error: function (pending_upload, guid, dfd, response) {
        if (pending_upload.get('had_local_guid')) {
            pending_upload.set({ local_guid: true, id: guid });
        }
        dfd.reject();
    },

    /**
     * Submit the form the server.  Only call this after all images
     * have been uploaded
     */
    _submit_pending_upload: function (pending_upload) {
        var this_ = this;
        var dfd = new $.Deferred();

        if (!this_.is_uploading) {
            // for concurrecy safety, check please
            dfd.reject();
            return dfd.promise();
        }

        var guid = this.get_upload_guid(pending_upload); // could be pending_upload.get('id') or a guid, etc.

        // safety mechanism to not allow us to re-upload previously submitted forms
        if (pending_upload.has_been_submitted && pending_upload.has_been_submitted()) {
            $f.mobile.logging.log_error('Attempting to submit previous submission!');
            this_.remove_records(guid, function () {
                dfd.resolve();
            });
        }

        // some structures (tasks, projects, fasttasks) have a local_guid flag. Unset it:
        if (pending_upload.get('local_guid')) {
            pending_upload.set('id', null);
            pending_upload.unset('local_guid');
            pending_upload.set('had_local_guid', true);
        }

        var save_options = {
            success: this_._on_upload_success.bind(this, pending_upload, guid, dfd),
            error: function (response) {
                this_._on_upload_error(pending_upload, guid, dfd, response);
            },
            timeout: 30000 // 30 sec
        };

        // collections do not have a native save(), so we're using our own that only takes an options {}
        if (pending_upload instanceof Backbone.Collection) {
            pending_upload.save(save_options);
        } else {
            pending_upload.save({}, save_options);
        }

        return dfd.promise();
    },

    get_all_records: function (success, error) {
        success = success || function () {};
        error = error || function () {};

        var this_ = this;
        var submissions = [];
        var submission_ids = this_.pluck('id');

        var recurse = function () {
            if (!submission_ids || submission_ids.length == 0) {
                return success(submissions);
            }

            var submission_id = submission_ids.pop();
            this_.get_record(
                submission_id,
                null,
                function (collection) {
                    if (!collection) {
                        // just in case collection is null // @jira:MOBILE-633
                        $f.mobile.logging.log_error('No record was found for submission', {
                            submission_id: submission_id
                        });
                        return recurse();
                    }

                    submissions.push(collection);
                    recurse();
                },
                function (err) {
                    $f.mobile.logging.log_error('Error while retreiving a submission record with the id:', {
                        submission_id: submission_id
                    });
                    error(err);
                },
                false
            );
        };

        recurse();
    }
});

/**
 * An abstract class that Form Templates and drafts can extend
 */
export const AbstractFormStructure = LocalRecords.extend({
    model: FormStructureLocalRecord,

    /**
     * Pull out data from the form_data and format it for the local record so it can easily be displayed
     * without fetching the entire structure from the local cache.
     *
     * @param form_data (MobileFormStructure)
     * @pararm id (String) - guid
     * @param local_record (LocalRecord) - or null if new
     * @private
     *
     * @returns LocalRecord
     */
    _denormalize_form_structure_data: function (form_data, id, local_record, options) {
        options = options || {};
        var date_modified = null;
        try {
            date_modified = new Date(form_data.smetadata.get('date_modified').$date);
        } catch (e) {
            // leave date_modified null
        }

        var accounts = form_data.filter(function (field) {
            return field.get('type') === 'account' && field.get('value');
        });

        var account_name = accounts.length > 0 ? accounts[0].get('account').name : '';

        var updates = {
            account_name: account_name,
            date_modified: date_modified,
            title: form_data.metadata.get('title'),
            is_secure: form_data.metadata.get('is_secure')
        };

        var is_new = !local_record;
        if (is_new) {
            local_record = new LocalRecord({
                id: id,
                date: new Date().getTime()
            });
        }

        local_record.set(updates);
        if (form_data.smetadata.get('task')) {
            local_record.set('task', form_data.smetadata.get('task'));
        }
        if (options.is_synced != undefined) {
            local_record.set('is_synced', options.is_synced);
        }

        if (is_new) {
            this.add(local_record, { merge: true });
        }
    },

    /**
     * Takes either a Backbone Collection or an array of collections.
     * They will be inserted into the records collection itself and
     * persisted to disk.
     *
     * This method handles both Form Templates and Form Drafts. If
     * there is a GUID set, that will be the LocalRecord ID
     *
     * @param collections - single or array of local record models
     * @param success - cb
     * @param error -cb
     * @param options - options for how to treat the local record objects created around the local record models
     *
     */
    _add_records: function (collections, success, error, options) {
        options = options || {};
        success = success || function () {};
        error = error || function () {};
        var this_ = this;
        var trigger_reset = false;

        if (!(collections instanceof Array)) {
            collections = [collections];
        }

        var _recurse = function () {
            if (!collections || collections.length === 0) {
                if (trigger_reset) {
                    this_.trigger('reset');
                }
                return success(this_);
            }

            var collection = collections.pop();
            if (!(collection instanceof Backbone.Collection)) {
                collection = new this_.klass(collection);
            }

            var id = collection.smetadata.get('guid') || collection.metadata.get('rdbms_id');
            var existing_record = this_.get(id);
            var is_new = !existing_record;
            this_._denormalize_form_structure_data(collection, id, existing_record, options);

            if (is_new && !trigger_reset) {
                trigger_reset = true;
            }

            /**
             * If we fail to save the record itself and it's a new record, we need to remove it from the key-of-keys
             * list. This way, we don't show a new pending upload number if there isn't one.
             */
            var _on_fail_save = function () {
                // if the record was a new one, let's remove it so we don't show a new ghost pending upload
                // this_.denormalize_form_structure_data creates existing_record if it was missing (new)
                if (is_new) {
                    this_.remove(existing_record);

                    this_.save(
                        function () {
                            error();
                        },
                        function () {
                            error();
                        }
                    );
                    return;
                }

                error();
            };

            this_.save(
                function () {
                    $f.mobile.storage.set(
                        `${this_.cache_key}_${id}`,
                        collection.toJSON(),
                        function () {
                            _recurse();
                        },
                        function (err) {
                            err = err || {};
                            err.klass = this_.klass;
                            $f.mobile.logging.log_error('Failed to save local record in $f.mobile.storage', err);

                            _on_fail_save();
                        }
                    );
                    this_._garbage_collect();
                },
                function () {
                    $f.mobile.logging.log_error('Unable to save record in AbstractFormStructure._add_records()', {
                        klass: this_.klass
                    });

                    _on_fail_save();
                }
            );
        };

        _recurse();
    },

    /**
     * Updates a single record in local storage.
     */
    update_record: function (form_structure, callback) {
        this._add_records(form_structure, callback);
    },

    /**
     * A no-op, this are stored locally in most cases.  Callers may override
     */
    _fetch_record: function (id, options, success, error_callback) {
        success && success(null); // we can't actually fetch these from disk, so let's just give the call null
    },

    /**
     * Grab information from the local device such as time, user agent, and GPS
     */
    add_local_metadata: function (collection, callback) {
        // don't allow anyone to overwrite data at the moment.  be sure not to use
        // date_created because drafts have date_created set when they are saved
        callback = callback || function () {};

        if (collection.smetadata.get('user_agent')) {
            return callback();
        }

        collection.smetadata.set({
            environment: isWeb() ? 'web' : 'app',
            platform: $f.mobile.settings.get_platform_type('unknown'),
            user_agent: navigator.userAgent
        });

        $f.mobile.get_device_location({
            success: function (position) {
                collection.smetadata.set('lat', position.coords.lat);
                collection.smetadata.set('lon', position.coords.lon);
            },
            error: function (error) {
                return;
            },
            complete: function () {
                callback();
            }
        });
    },

    _create_pending_file_uploads: function (submission) {
        submission.each(function (model) {
            // Remove unwanted field metadata
            if (
                model.get('type') == 'section' &&
                model.get('metadata') &&
                typeof model.get('metadata').is_collapsed !== 'undefined'
            ) {
                delete model.get('metadata').is_collapsed;
            }

            if (model instanceof MobileMediaField) {
                model.queue_field_files_for_upload(); // only ImageAndVideoField's support uploading files
            }
        });
    }
});

/**
 * Form Drafts
 */
export const FormDrafts = AbstractFormStructure.extend({
    keep_records: true,
    draft_count: 0, // get count with separate API call.
    comparator: function (draft) {
        return -new Date(draft.get('date_modified'));
    },

    initialize: function () {
        var this_ = this;
        this.on('add remove', function () {
            this_.draft_count = this.length;
        });
    },

    /**
     * Set a GUID, unique for this draft, then add it to the collection
     */
    add_record: function (collection, success, error, options) {
        success = success || function () {};
        error = error || function () {};
        options = options || {};

        collection.set_guid(); // for safety.  should have been set before it gets here
        collection.smetadata.set('draft', true);
        this._add_records(collection, success, error, options);
    },

    get_upload_guid: function (pending_submission) {
        return pending_submission.smetadata.get('guid');
    },

    /**
     * Determine if the active submission is a draft to be synced
     */
    is_active_collection_pending_draft: function () {
        var window_collection = $f.mobile.get_window_collection();
        return window_collection && window_collection.smetadata && window_collection.smetadata.get('guid');
    },

    _get_field_models: function (pending_upload) {
        return pending_upload.models;
    },

    _on_upload_success: function (pending_upload, guid, dfd, response) {
        var this_ = this;
        // set the new draft version from the server response.
        if (response.new_draft_version) {
            this.get_record(guid, {}, function (record) {
                if (!record) {
                    $f.mobile.logging.log_error('No record found for draft during draft _on_upload_success');
                    return;
                }
                record.smetadata.set('draft_version', response.new_draft_version);
                // nothing to be done if this fails to update.
                this_.update_record(record, function () {});
            });
        }
        var record = this.get(guid);

        if (record) {
            record.set('is_synced', true);
            this.save();
        }
        dfd.resolve();
    },

    _on_upload_error: function (pending_upload, guid, dfd, response) {
        // in this case it's already been updated on the server and requires override or cancel
        if (response && response.already_updated) {
            $f.mobile.uploads.clear_pending_file_uploads(guid);
            $f.mobile.form_drafts.handle_data_override(
                guid,
                response.current_draft_version,
                response.current_date_modified.$date
            );

            // don't fail the entire chain of uploads
            dfd.resolve();
            return;
        }
        dfd.reject();
    },

    /**
     * Get a draft from a form id
     */
    get_task_draft: function (rdbms_id, task_id, success_callback, empty_callback) {
        success_callback = success_callback || function () {};
        empty_callback = empty_callback || function () {};

        var _success_callback = success_callback;
        success_callback = function (record) {
            if (!record) {
                $f.mobile.logging.log_error('No record found for draft during get_task_draft');
            }

            _success_callback(record);
        };

        // form id and task id are required
        if (!rdbms_id || !task_id) {
            return empty_callback();
        }

        var with_delimiter = rdbms_id + '-';
        var has_drafts = _.filter($f.mobile.form_drafts.models, function (form_draft) {
            return (
                form_draft.id.indexOf(with_delimiter) > -1 &&
                form_draft.attributes.task &&
                form_draft.attributes.task.id == task_id
            );
        });

        // get most recent draft
        var draft_form = has_drafts.length ? has_drafts[has_drafts.length - 1] : false;

        if (draft_form) {
            return this.get_record(draft_form.id, null, success_callback);
        }

        // didn't find a draft
        empty_callback();
    },

    get_user_draft_count: function (success_cb) {
        var this_ = this;
        $f.mobile.ajax({
            url: '/api/v2/forms/drafts/?only_meta=true',
            dataType: 'json',
            type: 'GET',
            success: function (response) {
                this_.draft_count = response.meta.count;
                success_cb();
            },
            error: function (response) {
                /* do nothing, this is okay */
            }
        });
    },

    /**
     * This method will hit our server and fetch the drafts we don't have in our local cache.
     */
    get_user_drafts: function () {
        var this_ = this;
        var exclude_ids = [];
        var existing_ids = [];
        this.forEach(function (rec) {
            var id = rec.get('id');
            if (rec.get('is_synced') == false) {
                return exclude_ids.push(id);
            }
            existing_ids.push(id);
        });

        $f.mobile.ajax({
            url: '/api/v1/forms/drafts/list/',
            dataType: 'json',
            type: 'POST',
            cache: false,
            data: { exclude_ids: JSON.stringify(exclude_ids), existing_ids: JSON.stringify(existing_ids) },
            success: function (response) {
                var drafts = [];
                _.each(response.add, function (draft_data) {
                    var draft_collection = new FormDraft(draft_data);
                    drafts.push(draft_collection);
                });
                this_._add_records(
                    drafts,
                    function () {},
                    function () {},
                    { is_synced: true }
                );
                if (response.remove) {
                    this_.remove_records(response.remove);
                }
            },
            error: function (response) {
                /* do nothing, this is okay if they are offline, or not logged in */
            }
        });
    },

    /**
     * Handle the case where we attempted to save a draft and it has been updated from a different source.
     *
     * @param id - guid
     * @param current_draft_version (int)
     * @param current_date_modified (int) - epochts
     */
    handle_data_override: function (id, current_draft_version, current_date_modified) {
        var this_ = this;

        var date_modified_str = moment(new Date(current_date_modified)).format('MMMM D, h:mm A');
        var _t_title = _t('mobile_common_button_save_draft');
        var _t_explanation = _t('mobile_message_draft_override_message', { date: date_modified_str });
        var _t_override = _t('mobile_common_override');
        var _t_reload = _t('mobile_common_reload');
        var _t_cancel = _t('mobile_action_sheet_cancel');

        /**
         * Force an override by upping the local value of draft_version to be greater than the servers, which will
         * override the last one saved in the backend.
         */
        var override_fn = function () {
            this_.get_record(id, {}, function (record) {
                // not quite sure why we wouldn't have a record here. No graceful thing to do for the user
                if (!record) {
                    $f.mobile.logging.log_error('No record found for draft during draft override_fn');
                    return $f.mobile.form_drafts.get_user_drafts();
                }
                record.smetadata.set('draft_version', current_draft_version + 1);
                this_.update_record(record, function () {
                    $f.mobile.form_drafts.set_is_uploading(false);
                    $f.mobile.uploads.start_upload();
                });
            });
        };

        /**
         * Reload the draft by deleting the local record and re-fetching.
         */
        var reload_fn = function () {
            this_.remove_records([id], function () {
                // hide the uploading message and don't show upload_success
                getActiveToastView().hide_notification();
                $f.mobile.notifications.showUploadSuccessMessage = false;
                $f.mobile.form_drafts.get_user_drafts();
            });
        };

        // 1 = override, 2 = reload, 3 = cancel
        var decide_cb = function (button_idx) {
            if (button_idx === 1) {
                return override_fn();
            }
            if (button_idx === 2) {
                return reload_fn();
            }
        };

        if (isWeb()) {
            // @author:david we really shouldn't be using view code from here
            $f.application.content_overlay.show(
                new ModalOptionsView({
                    text: _t_explanation,
                    top: '20%',
                    right: 0,
                    buttons: [_t_override, _t_reload, _t_cancel],
                    callback: decide_cb
                })
            );
            return;
        }

        var options = {
            title: _t_title,
            subtitle: _t_explanation,
            position: [50, 60],
            buttonLabels: [_t_override, _t_reload],
            androidTheme: CordovaPlugins().actionsheet.ANDROID_THEMES.THEME_DEVICE_DEFAULT_LIGHT,
            androidEnableCancelButton: true,
            addCancelButtonWithLabel: _t_cancel
        };
        actionSheet(options, decide_cb);
    }
});

/**
 * A mechanism to our pending uplaods
 */
export const PendingFormSubmissions = AbstractFormStructure.extend({
    add_record: function (collection, success, error) {
        success = success || function () {};
        error = error || function () {};

        var this_ = this;
        collection.set_guid(); // for safety.  should have been set before it gets here
        var guid = collection.smetadata.get('guid');

        this.add_local_metadata(collection, function () {
            if (collection.is_draft()) {
                $f.mobile.form_drafts.get_record(guid, {}, function (record) {
                    // remove the draft from the server.
                    if (!record) {
                        $f.mobile.logging.log_error('No record was found for draft', {
                            submission_guid: guid
                        });

                        return;
                    }
                    record.delete();
                    $f.mobile.form_drafts.remove_records(guid);
                });
            }
            this_._add_records(
                collection,
                function () {
                    $f.application.vent.trigger('mobile:set_num_uploads');
                    success();
                },
                error
            );
        });
    },

    get_upload_guid: function (pending_submission) {
        return pending_submission.smetadata.get('guid');
    },

    _get_field_models: function (pending_upload) {
        return pending_upload.models;
    }
});

/**
 * Our mechanism for storing Form Templates on disk.  This also fetches information
 * such as current app store version, etc.  This is the most trafficked URL on all
 * of Mobile
 */
export const FormTemplatesLocal_record = AbstractFormStructure.extend({
    /**
     * Let's do some checking for some bad signs that we shouldn't see when retreiving templates from local!
     */
    get_record: function (id, options, success_callback, error_callback, force, never_miss) {
        success_callback = success_callback || function () {};

        var _success = success_callback;
        success_callback = function (collection) {
            if (collection.smetadata.get('date_submitted')) {
                $f.mobile.logging.log_error(
                    'CRITICAL: FormTemplate retrieved from cache with date_submitted! See attached smetadata.',
                    collection.smetadata.toJSON()
                );
            }
            if (collection.smetadata.get('guid')) {
                $f.mobile.logging.log_error(
                    'Warning: FormTemplate retrieved from cache with guid! See attached smetadata.',
                    collection.smetadata.toJSON()
                );
            }
            if (collection.smetadata.get('date_created')) {
                $f.mobile.logging.log_error(
                    'Warning: FormTemplate retrieved from cache with date_created! See attached smetadata.',
                    collection.smetadata.toJSON()
                );
            }
            if (collection.smetadata.get('date_modified') && !collection.smetadata.get('date_created')) {
                $f.mobile.logging.log_error(
                    'Warning: FormTemplate retrieved from cache with date_modified but not date_created! See attached smetadata.',
                    collection.smetadata.toJSON()
                );
            }

            _success(collection);
        };

        FormTemplatesLocal_record.__super__.get_record.apply(this, arguments);
    },

    /**
     * Our super class will call this when all other cahes fail, or if there
     * is a force.
     */
    _fetch_record: function (rdbms_id, options, success_callback, error_callback) {
        success_callback = success_callback || function () {};
        error_callback = error_callback || function () {};

        var parent = this;

        const requestUrl = '/api/v1/forms/get_template/' + rdbms_id;

        $f.mobile.ajax({
            url: requestUrl,
            dataType: 'json',
            type: 'GET',
            cache: false,
            data: { version: $f.version.id },
            success: function (response) {
                // do not save the same collection to disk that we send to disk in case the callback modifies it
                success_callback(new parent.klass(response));
                parent._add_records(new parent.klass(response));
            },
            error: function (response) {
                if (response && response.status == 403) {
                    $f.application.vent.trigger('mobile:remove_rdbms_id', rdbms_id);
                }

                error_callback();
            }
        });
    },

    /**
     * This method will hit our server and compare the templates we have in our
     * cache with the templates the server knows about.
     *
     * @param callback - to be called after the work is done
     */
    get_user_templates: function (callback) {
        var parent = this;
        var add_templates = Object();
        var delete_templates = [];

        var _add_reset_templates = function () {
            var collections = [];
            $.each(add_templates, function (key, template) {
                collections.push(new MobileFormStructure(JSON.parse(template.data)));
            });
            parent._add_records(collections, callback);
        };

        var current_templates = parent.pluck('id');

        if (!$f.active_user.is_logged_in()) {
            return;
        }

        const requestUrl = '/api/v1/forms/get_user_templates/';

        $f.mobile.ajax({
            url: requestUrl,
            dataType: 'json',
            type: 'POST',
            cache: false,
            data: { check: JSON.stringify(current_templates), version: $f.version.id },
            success: function (response) {
                add_templates = response.add;
                delete_templates = response.remove;

                // only update forms list if we got a change response from server
                if (_.isEmpty(delete_templates) === false || _.isEmpty(add_templates) === false) {
                    parent.remove_records(delete_templates, _add_reset_templates);
                } else if (callback) {
                    callback();
                }

                if (isIos()) {
                    $f.mobile.settings.set('latest_appstore_version', response.latest_appstore_version_ios);
                } else if (isAndroid()) {
                    $f.mobile.settings.set('latest_appstore_version', response.latest_appstore_version_android);
                } else if (isWindows()) {
                    $f.mobile.settings.set('latest_appstore_version', response.latest_appstore_version_windows);
                }
            },

            error: function (response) {
                /* do nothing, this is okay if they are offline, or not logged in */
            }
        });
    }
});

/**
 * Our cache object for tasks on the phone.
 */
export const TasksLocal_record = LocalRecords.extend({
    model: LocalRecord.extend({
        defaults: {
            title: null,
            account__name: null,
            distance: null,
            date_due: null,
            date: null // this is used for cache invalidation
        },

        /**
         * This is annoying, but we need to be able to call this method from normal task models for local record tasks.
         * Once we refactor our task caching to use cache v2, this won't be an issue, but for now, we'll just forward any
         * calls to a temporary task object with our attributes.
         */
        get_time_due_str: function () {
            var tmp_task = new MobileTask(this.attributes);
            return tmp_task.get_time_due_str.apply(tmp_task, arguments);
        }
    }),

    paginator: null,

    comparator: function (model) {
        if (!model.get('date_due')) {
            return null;
        }
        return model.get('date_due').$date;
    },

    initialize: function () {
        this.paginator = new Paginator({ order_by: 'distance', filter: 'me', status: 'open' });
    },

    /**
     * Adds/updates records.
     * @param {Backbone.Collection/Model}   collection                      The model/collection to save
     * @param {function}                    success                         The function to call on success.
     * @param {function}                    error                           The function to call on error.
     * @param {dict}                        options                         Additional Options
     */
    _add_records: function (collection, success, error, options) {
        success || (success = function () {});
        error || (error = function () {});
        options || (options = function () {});
        var parent = this;

        if (collection instanceof Backbone.Model) {
            collection = new MobileTasks(collection.toJSON());
        }

        var recurse = function () {
            if (!collection || collection.length == 0) {
                parent.save(function () {
                    if (success) {
                        success(parent);
                    }
                    parent._garbage_collect();
                }, error);
                return;
            }

            var model = collection.shift();

            if (parent.findWhere({ id: model.get('id') })) {
                parent.update_record(model, recurse);
                return;
            }

            parent.add(
                new parent.model({
                    id: model.id,
                    title: model.get('title'),
                    account__name: model.get('account') ? model.get('account').get('name') : null,
                    date_due: model.get('date_due') ? { $date: new Date(model.get('date_due').$date).getTime() } : null,
                    distance: model.get('distance'),
                    date: new Date().getTime()
                }),
                { merge: true }
            );

            $f.mobile.storage.set(parent.cache_key + '_' + model.id, model.toJSON(), recurse);
        };
        recurse();
        // Force a sort at the end in cause accounts (distance) has changed with updated tasks
        this.sort();
    },

    add_records: function (model, success, error, options) {
        this._add_records(model, success, error, options);
    },

    /**
     * Updates a local record with a specified ID.
     * @param  {Backbone.Model}     model       The new model to update the old model with
     * @param  {Function}           callback    The callback to fire once the save has completed
     */
    update_record: function (model, callback) {
        callback = callback || function () {};
        var old_record = this.get(model.get('id'));

        if (!old_record) {
            // can't find record - we must not be caching it; just return
            callback();
            return;
        }

        var this_ = this;

        // Update local record with data, then save model to NativeStorage
        old_record.set(
            {
                id: model.id,
                title: model.get('title'),
                account__name: model.get('account') ? model.get('account').get('name') : null,
                date_due: model.get('date_due') ? { $date: new Date(model.get('date_due').$date).getTime() } : null,
                distance: model.get('distance') ? model.get('distance') : old_record.get('distance'), // don't overwrite distance if it's null (sometimes we don't request distance)
                date: new Date().getTime()
            },
            { merge: true }
        );

        // get old model
        this.get_record(old_record.get('id'), null, function (old_model) {
            if (!old_model) {
                $f.mobile.logging.log_error('No record found for old model during update_record', {
                    record_id: old_record.get('id')
                });
            }
            old_model.set(model.toJSON());
            $f.mobile.storage.set(this_.cache_key + '_' + model.id, old_model.toJSON(), function () {
                callback();
            });
        });
    },

    fetch: function (options) {
        var parent = this;
        var old_options = options ? options : (options = {});
        options = old_options ? _.clone(old_options) : {}; // need to clone extend success callback

        options.success = function (collection) {
            parent._add_records(collection, null, null);
            if (old_options.success) {
                old_options.success(collection);
            }
        };

        new MyTasks([], { paginator: this.paginator }).fetch(options);
    }
});

export const ProcessingTasks = TasksLocal_record.extend({
    MAX_AGE_MINUTES: 10080, // 7 days

    /**
     * Adds a record to a LocalRecords instances (e.g., $f.mobile.pending_tasks_in_processing).
     *
     * This wraps LocalRecords.add_record() but uses a promise instead of callbacks. Eventually we'll
     * update add_records() to return a promise, but this helps for now.
     *
     * @param  {LocalRecords}   local_records_instance   The instance of the LocalRecords collection (usually $f.mobile.*)
     * @param  {LocalRecord}    records                   The model to add as a record.
     *
     */
    add_records_with_promise: function (records) {
        return new Promise((resolve, reject) => {
            this.add_records(records, resolve, reject);
        });
    },

    /**
     * Our 'submission processing' queue doesn't always know when tasks are completed, since the server may never tell
     * us if the filters no longer reveal it. Let's clear any out that are too old for us to care about.
     */
    clear_stale_records: async function () {
        const ids_to_remove = this.filter((record) => record.get_age_minutes() > this.MAX_AGE_MINUTES).map(
            (record) => record.id
        );

        return new Promise((resolve) => {
            this.remove_records(ids_to_remove, resolve);
        });
    }
});

export const PendingTasks = TasksLocal_record.extend({
    get_upload_guid: function (task) {
        return task.get('id');
    },

    _get_field_models: function (pending_upload) {
        return pending_upload.get('fulfillment').get('fields');
    },

    _create_pending_file_uploads: function (task) {
        // If this is not a 'quick' task, there are no fields to upload!
        if (task.get('reply').get('type') != 'quick') {
            return;
        }

        _.each(task.get('fulfillment').get('fields'), function (model) {
            model.queue_field_files_for_upload();
        });
    }
});

const UploadableTaskMixin = {
    _get_field_models: function (pending_upload) {
        return [pending_upload.get('title_image_field')];
    },

    prepare_pending_upload_for_submission: function (pending_upload) {
        LocalRecords.prototype.prepare_pending_upload_for_submission.apply(this, arguments);

        var title_image_field = pending_upload.get('title_image_field');
        pending_upload.set('title_image', title_image_field.get('value'));

        pending_upload.unset('title_image_field');
    },

    _create_pending_file_uploads: function (task) {
        var field = task.get('title_image_field');

        // If this is a form_submission, there are no fields to upload!
        if (task.get('reply').get('type') == 'form_submission' || !field) {
            return;
        }

        task.get('title_image_field').queue_field_files_for_upload();
    }
};

/**
 * Really, the only way to create new tasks on mobile is via FastTasksLocal_record.
 * This is used for editing single tasks.
 */
export const NewTasks = TasksLocal_record.extend(
    _.extend({}, UploadableTaskMixin, {
        /**
         * This method will look for pending tasks in the queue given their id. It relies only on $f.mobile.storage.
         * @param task_id
         * @param callback
         */
        get_pending_task: function (task_id, callback) {
            $f.mobile.storage.get(
                `${this.cache_key}_${task_id}`,
                function (collections_json) {
                    callback(collections_json);
                },
                function (error) {
                    callback(null);
                }
            );
        },

        add_records: function (model, success, error, options) {
            if (model.isNew()) {
                $f.mobile.uploads.set_model_guid(model); // setting temporary ID for use by local storage and pending uploads
            }

            NewTasks.__super__.add_records.call(this, model, success, error, options);
        },

        get_upload_guid: function (task) {
            return task.get('id');
        }
    })
);

export const NewProjects = LocalRecords.extend({
    _fetch_record: function (id, options, success_callback, error_callback, cached_object) {
        throw new Error('Programmer error: There should be no re-fetch of a local_record for projects');
    },
    /**
     * Adds/updates records.
     * @param {Backbone.Collection/Model}   collection                      The model/collection to save
     * @param {function}                    success                         The function to call on success.
     * @param {function}                    error                           The function to call on error.
     * @param {dict}                        options                         Additional Options
     */
    _add_records: function (model, success, error, options) {
        success = success || function () {};
        error = error || function () {};
        options = options || function () {};

        var this_ = this;
        var now = new Date();

        if (!model.id) {
            $f.mobile.uploads.set_model_guid(model);
        }

        this.add(
            new LocalRecord({
                id: model.get('id'),
                title: model.get('title'),
                date: now.getTime()
            })
        );

        this.save(function () {
            $f.mobile.storage.set(
                `${this_.cache_key}_${model.get('id')}`,
                model.toJSON(),
                function () {
                    if (success) {
                        success(this_);
                    }
                },
                error
            );
            this_._garbage_collect();
        }, error);
    },

    add_records: function (collection, success, error, options) {
        this._add_records(collection, success, error, options);
    },

    update_record: function (model, callback) {
        // dont implement, just skip
        callback();
    },

    get_upload_guid: function (project) {
        return project.get('id');
    },

    _get_field_models: function (pending_upload) {
        return [pending_upload.get('create_tasks_data').get('title_image_field')];
    },

    prepare_pending_upload_for_submission: function (pending_upload) {
        NewProjects.__super__.prepare_pending_upload_for_submission.apply(this, arguments);

        var title_image_field = pending_upload.get('create_tasks_data').get('title_image_field');
        pending_upload.get('create_tasks_data').set('title_image', title_image_field.get('value'));

        pending_upload.get('create_tasks_data').unset('title_image_field');
    },

    _create_pending_file_uploads: function (project) {
        var field = project.get('create_tasks_data').get('title_image_field');

        // If this is a form_submission, there are no fields to upload!
        if (project.get('create_tasks_data').get('reply').get('type') == 'form_submission' || !field) {
            return;
        }

        field.queue_field_files_for_upload();
    }
});

export const FastTasksLocal_record = LocalRecords.extend(
    _.extend({}, UploadableTaskMixin, {
        _fetch_record: function (id, options, success, error_callback) {
            success && success(null); // we can't actually fetch these from disk, so let's just give the call null
        },

        _add_records: function (model, success, error, options) {
            success = success || function () {};
            error = error || function () {};
            options = options || function () {};

            var this_ = this;
            var now = new Date();

            $f.mobile.uploads.set_model_guid(model);

            this.add(
                new LocalRecord({
                    id: model.get('id'),
                    date: now.getTime()
                })
            );

            this.save(function () {
                $f.mobile.storage.set(
                    `${this_.cache_key}_${model.get('id')}`,
                    model.toJSON(),
                    function () {
                        success(this_);
                    },
                    error
                );
                this_._garbage_collect();
            }, error);
        },

        add_records: function (model, success, error, options) {
            this._add_records(model, success, error, options);
        },

        update_record: function (model, callback) {
            // dont implement, just skip
            callback();
        },

        get_upload_guid: function (fast_tasks) {
            return fast_tasks.get('id');
        },

        _create_pending_file_uploads: function (fast_tasks) {
            fast_tasks.get('tasks').each(function (task) {
                var field = task.get('title_image_field');

                // If this is a form_submission, there are no fields to upload!
                if (task.get('reply').get('type') !== 'quick' || !field) {
                    return;
                }

                task.fast_tasks_model = fast_tasks; // this is needed by ImageAndVideoFieldForTask.get_parent_upload_guid()
                task.get('title_image_field').queue_field_files_for_upload();
            });
        },

        _get_field_models: function (pending_upload) {
            var models = [];

            pending_upload.get('tasks').each(function (task) {
                models.push(task.get('title_image_field'));
            });

            return models;
        },

        prepare_pending_upload_for_submission: function (pending_upload) {
            FastTasksLocal_record.__super__.prepare_pending_upload_for_submission.apply(this, arguments);

            pending_upload.get('tasks').each(function (task) {
                var title_image_field = task.get('title_image_field');
                task.set('title_image', title_image_field.get('value'));

                task.unset('title_image_field');
            });
        }
    })
);
